
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@/types/user';
import { useToast } from '@/hooks/use-toast';

export async function getAllOrganizationUsers(): Promise<User[]> {
  const { data, error } = await supabase
    .from('organization_members')
    .select(`
      *,
      organizations (
        name
      )
    `)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching all organization users:', error);
    throw error;
  }

  return (data || []).map(member => ({
    id: member.id,
    name: member.full_name || member.email,
    email: member.email,
    role: (member.role.charAt(0).toUpperCase() + member.role.slice(1)) as 'Admin' | 'Editor' | 'Viewer',
    status: member.is_active ? 'Active' : 'Inactive',
    lastActive: member.updated_at,
    invitationStatus: member.invitation_status as 'pending' | 'accepted' | 'expired',
    invitedAt: member.invited_at,
    invitedBy: member.invited_by,
    user_type: member.user_type as 'org_admin' | 'team_member',
    organization_id: member.organization_id,
    organization_name: member.organizations?.name
  }));
}

export function useAllOrganizationUsers() {
  const { toast } = useToast();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { 
    data: users = [], 
    isLoading: loading, 
    refetch
  } = useQuery({
    queryKey: ['all-organization-users', refreshTrigger],
    queryFn: getAllOrganizationUsers,
  });

  // Set up real-time subscription for organization_members table
  useEffect(() => {
    console.log('🔗 Setting up real-time subscription for all organization_members...');
    const channel = supabase
      .channel('all-organization-members-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'organization_members'
        },
        (payload) => {
          console.log('🔴 Real-time update received for all organization_members:', payload);
          setRefreshTrigger(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      console.log('🔌 Cleaning up real-time subscription for all organization_members');
      supabase.removeChannel(channel);
    };
  }, []);

  const handleUserUpdated = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleUserInvited = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleManualRefresh = () => {
    refetch();
    toast({
      title: "Success",
      description: `Refreshed successfully. Found ${users.length} organization users.`,
    });
  };

  return {
    users,
    loading,
    refreshing: false,
    handleUserUpdated,
    handleUserInvited,
    handleManualRefresh
  };
}
